import type { CallLogCategoryType, CallLogType } from 'Components/CallLogs/types';
import { useResetMissedCalls } from 'Hooks/badgeCounters';
import { useBulkDeleteCallLogs } from 'Modules/callLogs/index.request';
import { useNotificationStore } from 'Modules/notification/store';
import { useConfirmationModalStore } from 'Modules/ui/modals/store';
import React from 'react';
import {
  ANALYTICS_EVENT_CALL_LOG_DELETE_FAILED,
  ANALYTICS_EVENT_CALL_LOG_DELETE_SUCCESS,
  pushToGTMDataLayer,
} from 'Utils/analytics';
import { useCallLogSelection } from './useCallLogsSelection';

/**
 * Manages the bulk-delete flow for the call logs list, including selection
 * state, the shared confirmation modal, the delete mutation, and feedback.
 *
 * When the API reports partial failures, only the failed ids remain selected
 * so the user can retry them. If the delete request fails entirely, the
 * original selection is preserved for retry. Selection mode is exited only
 * after all selected call logs were deleted successfully.
 */
export const useCallLogsBulkDelete = (typeOfCallId: CallLogCategoryType) => {
  const {
    selectedIds,
    selectedCount,
    isSelecting,
    isMaxReached,
    startSelecting,
    cancelSelecting,
    toggleSelect,
    selectAllVisible,
    resetSelection,
    replaceSelection,
  } = useCallLogSelection();

  const bulkDelete = useBulkDeleteCallLogs();
  const resetMissedCallsBadge = useResetMissedCalls();
  const { setOpenConfirmationModal } = useConfirmationModalStore();

  // Clear selection when switching between call-log filters.
  React.useEffect(() => {
    cancelSelecting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeOfCallId]);

  // Shows the failure notification with an option to retry the failed ids.
  const notifyDeleteFailed = (idsToRetry: CallLogType['id'][]) => {
    pushToGTMDataLayer(ANALYTICS_EVENT_CALL_LOG_DELETE_FAILED);

    const { addNotify, removeNotify } = useNotificationStore.getState();
    // Use the notification's stack position so it can be removed by its id
    // on Retry.
    const position = 'tr';

    const notificationId = addNotify({
      type: 'warning',
      autoDismiss: true,
      dismissible: true,
      position,
      title: 'Action failed',
      message: 'Failed to delete call logs.',
      action: {
        label: 'Retry',
        onClick: () => {
          removeNotify(position, notificationId);
          runBulkDelete(idsToRetry);
        },
      },
    });
  };

  // Runs the bulk delete for the given ids. Shared by the initial delete
  // and the "Retry" action so both use the same success/error handling.
  const runBulkDelete = (ids: CallLogType['id'][]) => {
    if (bulkDelete.isPending) {
      return;
    }

    bulkDelete.mutate(ids, {
      onSuccess: (data) => {
        // The bulk delete endpoint can partially succeed. On partial failure,
        // keep only the failed ids selected so the user can retry just those.
        if (data.failed.length > 0) {
          replaceSelection(data.failed);
          notifyDeleteFailed(data.failed);
          return;
        }

        pushToGTMDataLayer(ANALYTICS_EVENT_CALL_LOG_DELETE_SUCCESS);

        const deletedCount =
          data.requestedIds.length - data.failed.length;

        useNotificationStore.getState().addNotify({
          type: 'success',
          autoDismiss: true,
          dismissible: true,
          title: 'Success',
          message:
            deletedCount === 1
              ? '1 call log deleted'
              : `${deletedCount} call logs deleted`,
        });

        // Clear the missed-calls badge so it does not stay stale after deletion.
        resetMissedCallsBadge.mutate();

        // Exit selection mode only when all selected call logs were deleted.
        cancelSelecting();
      },
      onError: () => {
        // If the delete request fails, preserve the ids from this attempt so the
        // user can retry the operation.
        notifyDeleteFailed(ids);
      },
    });
  };

  // Open the confirmation modal for the selected call logs.
  const handleRequestDelete = () => {
    if (bulkDelete.isPending) {
      return;
    }

    const ids = Array.from(selectedIds);
    const count = ids.length;

    setOpenConfirmationModal({
      title:
        count === 1
          ? 'Are you sure you want to delete this call log?'
          : `Are you sure you want to delete these ${count} call logs?`,
      description: "This action can't be undone.",
      testid: 'deleteCallLogsModal',
      textCancel: 'Cancel',
      textConfirm: 'Delete',
      onConfirm: () => {
        runBulkDelete(ids);
      },
    });
  };

  return {
    selectedIds,
    selectedCount,
    isSelecting,
    isMaxReached,
    startSelecting,
    cancelSelecting,
    toggleSelect,
    selectAllVisible,
    resetSelection,
    isDeleting: bulkDelete.isPending,
    handleRequestDelete,
  };
};
